// Replay research outcomes (in-sample, NOT live), the post-entry timeline, and caveats worth knowing.
interface Arm { arm: string; sharpe: string; equity: string; dd: string; note: string }

const ARMS: Arm[] = [
  { arm: 'Live-aligned baseline', sharpe: '0.742', equity: '$592k', dd: '-15.75%', note: 'Reference for the arms below.' },
  { arm: '3% cap arm (adopted, D080)', sharpe: '0.958', equity: '$609k', dd: '-11.7%', note: 'Missed 2 of its 7 pre-registered checks; Jeff adopted it anyway.' },
  { arm: 'Earlier baseline (superseded)', sharpe: '1.053', equity: '$741k', dd: 'n/a', note: 'Overstated: the replay sized commodities against full capital.' },
]
const REJECTED = ['range gate', 'risk-to-stop sizing', 'sleeve bands', 'heat limit', 'replace-weakest']

const PHASES: { h: string; p: string; crs?: boolean }[] = [
  { h: 'Day 0: entry', p: 'Market-on-open buy plus a resting stop. ETF / stock / IPO stop = highest of anchored VWAP, 20-day low, hard floor. Commodity / crypto = close minus 2 x ATR. The stop is placed once the buy fills (arm-on-fill, D843/D081, live 2026-10-29; IPO already did this, D856).' },
  { h: 'The gap: on the initial stop', p: 'Nothing re-checks the name here. It sits on its first stop until it hits it or reaches activation. One pyramid add at +3R (half size). This is CRS territory.', crs: true },
  { h: 'Trail activates', p: 'At avg cost + 4.0R x regime scalar (D075). Below a 1.0 scalar it activates sooner, above it later.' },
  { h: 'Trailing', p: 'Stop = VWAP from entry minus 1.5 x ATR (ETF) or 2.0 x ATR (others). Only ratchets up, minimum step max(0.1 ATR, 0.25%).' },
]

const WORTH_KNOWING: { b: string; t: string }[] = [
  { b: 'The 0-100 TrendQual score is not an entry gate.', t: 'Live entries use the pass/fail rungs. The score (enter 65 / exit 40) only feeds the ETF/stock regime breadth. The ranking "score" is the share of gates passed.' },
  { b: 'The range gate is gone (D079).', t: 'Removed from all sleeves. range_gate_* in dynamic_tq_params is diagnostic only and no order depends on it.' },
  { b: 'Sizing is a per-position cap (D080).', t: 'min(1.5% NAV for stock/IPO, 3% NAV for ETF/commodity/crypto, 20% of the sleeve\'s regime-scaled target). It replaces the fixed share per name.' },
  { b: 'Market-health anchors use the ETF settings in every sleeve.', t: 'The benchmark anchored VWAP is built with the ETF window: lowest daily low of the last 72 sessions, at least 12 sessions back. The original EasyLanguage used 150 sessions for stocks; the Python port simplified that.' },
  { b: 'Replay is not live in several places.', t: 'The IPO volume floor is 300k in replay (live 1M), and commodity/crypto benchmarks and ATR differ. Trail activation depends on the replay config. Any CRS replay inherits these.' },
  { b: 'ETF weekly scan filters are not used.', t: 'The ETF executor trades its fixed 20-name list; the scanner AUM/spread/ATR filters never touch it.' },
  { b: 'Earnings:', t: 'no earnings check at entry in any sleeve.' },
]

export function ResearchOutcomes({ narrow }: { narrow: boolean }) {
  return (
    <>
      <details className="strat-details" open={!narrow} key={`po${narrow}`}>
        <summary>After it buys: where CRS would live</summary>
        <ol className="strat-timeline">
          {PHASES.map(ph => (
            <li key={ph.h} className={ph.crs ? 'strat-phase strat-phase--crs' : 'strat-phase'}>
              <h4>{ph.h}</h4><p>{ph.p}</p>
            </li>
          ))}
        </ol>
        <p className="strat-note">
          Learned 2026-10-20: in the gap, a name that fades without hitting its stop has usually been pulling back inside a
          trend. In replay it beat its sleeve benchmark by +1.9% to +3.8% over the next 60 sessions, and the names bought to
          replace it did worse.
        </p>
      </details>

      <details className="strat-details" open={!narrow} key={`ro${narrow}`}>
        <summary>Research outcomes</summary>
        <p className="strat-caption"><b>Replay only, in-sample 2022 to 2026-04, dated 2026-10-29. These are not live results.</b></p>
        <div className="strat-table-wrap" tabIndex={0} role="region" aria-label="Replay research outcomes table">
          <table className="strat-table">
            <caption className="sr-only">Replay arms: Sharpe, final equity, max drawdown</caption>
            <thead><tr><th scope="col">Arm</th><th scope="col" className="num">Sharpe</th><th scope="col" className="num">Final equity</th><th scope="col" className="num">Max DD</th><th scope="col">Note</th></tr></thead>
            <tbody>
              {ARMS.map(a => (
                <tr key={a.arm}>
                  <th scope="row">{a.arm}</th>
                  <td className="num" data-label="Sharpe">{a.sharpe}</td>
                  <td className="num" data-label="Final equity">{a.equity}</td>
                  <td className="num" data-label="Max DD">{a.dd}</td>
                  <td className="strat-table-note">{a.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="strat-note">Rejected in replay: {REJECTED.join(', ')}.</p>
      </details>

      <details className="strat-details" open={!narrow} key={`wk${narrow}`}>
        <summary>Things worth knowing</summary>
        <ul className="strat-flags">
          {WORTH_KNOWING.map(w => <li key={w.b}><b>{w.b}</b> {w.t}</li>)}
        </ul>
      </details>
    </>
  )
}
